import { Button } from "@base/ui/Button/Button";
import { Dialog } from "@base/ui/Dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { deletePaymentMethod } from "@features/membership/api/paymentMethods";
import { toast } from "sonner";
import { membershipQueries } from "@features/membership/api/queryFactory";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  paymentMethodId: string;
};

const DeletePaymentMethodDialog = ({ isOpen, onClose, paymentMethodId }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (paymentMethodId: string) => deletePaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membershipQueries.paymentMethodsKey() });
      toast.success(t("paymentMethodDeletedSuccess"));
    },
    onError: () => {
      toast.error(t("paymentMethodDeleteFailed"));
    },
    onSettled: () => {
      onClose();
    },
  });

  const handleDelete = () => {
    mutate(paymentMethodId);
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Dialog.Content>
        <div className="flex flex-col gap-4 p-4 pt-6">
          <Dialog.Title>{t("deletePaymentMethodTitle")}</Dialog.Title>
          <Dialog.Description>{t("deletePaymentMethodDescription")}</Dialog.Description>
          <div className="flex gap-1 mt-2">
            <Button type="button" variant="ghost" color="secondary" className="w-full" onClick={onClose}>
              {t("keepPaymentMethod")}
            </Button>
            <Button type="button" variant="fill" color="danger" className="w-full" onClick={handleDelete}>
              {t("deletePaymentMethod")}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeletePaymentMethodDialog;
